import React from 'react';
import type { FrequencyType } from '../../types';
import { Calendar, Clock, BookOpen, AlertCircle } from 'lucide-react';

interface FrequencyTabsProps {
  activeFrequency: FrequencyType;
  onSelectFrequency: (freq: FrequencyType) => void;
  itemCounts?: {
    DAILY?: number;
    WEEKLY?: number;
    MONTHLY?: number;
    HOURS?: number;
  };
  onOpenLubricantGuide: () => void;
}

export const FrequencyTabs: React.FC<FrequencyTabsProps> = ({
  activeFrequency,
  onSelectFrequency,
  itemCounts = { DAILY: 9, WEEKLY: 3, MONTHLY: 5, HOURS: 9 },
  onOpenLubricantGuide,
}) => {
  const tabs: Array<{
    id: FrequencyType;
    labelTh: string;
    labelEn: string;
    count?: number;
    icon: React.ReactNode;
  }> = [
    {
      id: 'DAILY',
      labelTh: 'ประจำวันและกะ',
      labelEn: 'Daily & Shift',
      count: itemCounts.DAILY,
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      id: 'WEEKLY',
      labelTh: 'ประจำสัปดาห์',
      labelEn: 'Weekly',
      count: itemCounts.WEEKLY,
      icon: <Clock className="w-4 h-4" />,
    },
    {
      id: 'MONTHLY',
      labelTh: 'ประจำเดือน',
      labelEn: 'Monthly',
      count: itemCounts.MONTHLY,
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      id: 'HOURS',
      labelTh: 'รอบชั่วโมงการทำงาน',
      labelEn: 'Operating Hours',
      count: itemCounts.HOURS,
      icon: <AlertCircle className="w-4 h-4 text-amber-400" />,
    },
  ];

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
      {/* Horizontal scrolling tab list */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
        {tabs.map((tab) => {
          const isActive = activeFrequency === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onSelectFrequency(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm transition-all min-h-[48px] whitespace-nowrap select-none active:scale-95 ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm ring-1 ring-blue-500'
                  : 'bg-white text-slate-700 hover:text-slate-900 hover:bg-slate-50 border border-slate-200 shadow-2xs'
              }`}
            >
              {tab.icon}
              <div className="text-left">
                <span className="block leading-tight">{tab.labelTh}</span>
                <span className="text-[10px] opacity-75 font-normal block leading-tight">
                  {tab.labelEn}
                </span>
              </div>
              {tab.count !== undefined && (
                <span
                  className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-mono font-bold ${
                    isActive ? 'bg-blue-800 text-blue-100' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Lubrication Guide Button */}
      <button
        type="button"
        onClick={onOpenLubricantGuide}
        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 transition-all min-h-[48px] shrink-0 active:scale-95 shadow-2xs"
      >
        <BookOpen className="w-4 h-4 text-amber-600" />
        <span>คู่มือและคำแนะนำใช้น้ำมัน</span>
      </button>
    </div>
  );
};
